import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { LogoPill } from "./BrandWall";
import { usePageMeta } from "../hooks/usePageMeta";
import { CONTACT } from "../data/site";

const CategoryPage = ({ page, testId }) => {
  usePageMeta(page.seo.title, page.seo.description);
  const waText = encodeURIComponent(`Hi, I'm interested in ${page.eyebrow.toLowerCase()}. Please share prices.`);

  return (
    <div data-testid={testId}>
      <section className="relative overflow-hidden pb-16 pt-40 sm:pt-52">
        <div className="pointer-events-none absolute -right-40 top-24 h-96 w-96 rounded-full bg-brand/5 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-brand"
          >
            {page.eyebrow} · Sector 86, Greater Faridabad
          </motion.p>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl" data-testid={`${testId}-headline`}>
            {page.headline.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.215, 0.61, 0.355, 1] }}
                >
                  {i === page.accentLine ? <em className="text-brand-gradient not-italic">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-[#5C6570] sm:text-lg"
          >
            {page.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid={`${testId}-enquire-button`}
              className="group flex items-center gap-3 bg-brand px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:bg-brand-bright"
            >
              Enquire Now
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={`https://wa.me/919873628088?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testId}-whatsapp-button`}
              className="border border-ink/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:border-brand hover:text-brand"
            >
              WhatsApp for Prices
            </a>
          </motion.div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          {page.groups.map((group) => (
            <div key={group.title} className="mb-16 last:mb-0">
              <Reveal className="mb-10">
                <Eyebrow>{group.title}</Eyebrow>
              </Reveal>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {group.brands.map((b, i) => (
                  <Reveal key={b.name} delay={i * 0.08}>
                    <div
                      data-testid={`${testId}-brand-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="flex h-full flex-col gap-6 border border-ink/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand/50 sm:flex-row sm:items-center"
                    >
                      <LogoPill name={b.name} logo={b.logo} className="!px-5 !py-3" />
                      <div>
                        <h3 className="font-serif text-xl font-semibold text-foreground">{b.name}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-[#5C6570]">{b.line}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}

          <Reveal className="mt-4">
            <div className="flex flex-wrap gap-2" data-testid={`${testId}-chips`}>
              {page.chips.map((c) => (
                <span
                  key={c}
                  className="flex items-center gap-2 border border-ink/15 bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-[#5C6570]"
                >
                  <BadgeCheck size={13} className="text-brand" /> {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <Reveal>
            {page.seoContent.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-[#5C6570] first:mt-0">{p}</p>
            ))}
            <div className="mt-10 border border-brand/25 bg-white p-8 text-center sm:p-10">
              <p className="font-serif text-2xl font-medium text-foreground">
                Call {CONTACT.owner} for today's price list
              </p>
              <a
                href={CONTACT.phoneHref}
                data-testid={`${testId}-call-button`}
                className="mt-2 inline-block font-serif text-3xl font-semibold text-brand transition-colors hover:text-brand-bright"
              >
                {CONTACT.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;
